import { gql, useLazyQuery } from "@apollo/client";
import React, { useCallback, useRef, useState } from "react";

import {
  GetQuestionsOptionsPerOrgCoverageQuery,
  GetQuestionsOptionsPerOrgCoverageQueryVariables,
  QuestionAnalyticsCode,
} from "../../../../../../__generated__/graphql";
import { questionAnalyticsCodeDescriptions } from "../../../../graphql/values";
import Card from "../../../design-system/Card";
import Container, { ContainerSize } from "../../../design-system/Container";
import GenericError from "../../../design-system/GenericError";
import CombinedHeader from "../../../design-system/PageHeader/CombinedHeader";
import Select from "../../../design-system/Select";
import QuestionData from "./QuestionData";

export type Row = GetQuestionsOptionsPerOrgCoverageQuery["questionsOptionsPerOrgCoverage"]["rows"][number];

const QUERY = gql`
  query getQuestionsOptionsPerOrgCoverage($category: QuestionAnalyticsCode, $page: Int) {
    questionsOptionsPerOrgCoverage(category: $category, page: $page) {
      currentPage
      totalPages
      totalItems
      rows {
        organisationId
        organisationName
        questionTitle
        optionValue
        tag
        sector
        questionType
        optionId
        isFreeText
      }
    }
  }
`;
export default function OrganisationsQuestionData() {
  const [analyticsCode, setAnalyticsCode] = useState<QuestionAnalyticsCode | undefined>(undefined);
  const [allRows, setAllRows] = useState<Row[]>([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const requestIdRef = useRef(0);

  const [getQuery] = useLazyQuery<
    GetQuestionsOptionsPerOrgCoverageQuery,
    GetQuestionsOptionsPerOrgCoverageQueryVariables
  >(QUERY);

  const onChangeAnalyticsCode = useCallback(
    async (selection: string) => {
      const code = selection === "" ? undefined : (selection as QuestionAnalyticsCode);
      const requestId = ++requestIdRef.current;

      setAnalyticsCode(code);
      setAllRows([]);
      setError(undefined);

      if (!code) return;

      setLoadingAll(true);
      try {
        let page = 1;
        let collected: Row[] = [];
        for (;;) {
          const { data } = await getQuery({ variables: { category: code, page } });
          const result = data?.questionsOptionsPerOrgCoverage;
          if (!result) break;
          collected = collected.concat(result.rows);
          if (page >= result.totalPages) break;
          page += 1;
        }
        if (requestId === requestIdRef.current) setAllRows(collected);
      } catch (err) {
        if (requestId === requestIdRef.current) setError(err as Error);
      } finally {
        if (requestId === requestIdRef.current) setLoadingAll(false);
      }
    },
    [getQuery],
  );

  return (
    <Container size={ContainerSize.Medium}>
      <CombinedHeader
        title="Question data"
        breadcrumbs={[{ label: "Organisations data", url: "/organisations-data" }]}
      ></CombinedHeader>
      <Card compact>
        <Select label="Analytics code" value={analyticsCode || ""} onChange={onChangeAnalyticsCode}>
          <option value="" disabled>
            Choose question type
          </option>
          {questionAnalyticsCodeDescriptions.map(({ code }, i) => (
            <option key={i} value={code}>
              {code}
            </option>
          ))}
        </Select>
      </Card>

      <div>{error ? <GenericError error={error} /> : <QuestionData loading={loadingAll} rows={allRows} />}</div>
    </Container>
  );
}
